import * as Sentry from "@sentry/react";

type SentryLogMetadata = {
  action: string;
  area: string;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
  level?: Sentry.SeverityLevel;
  source?: string;
  tags?: Record<string, string>;
};

/**
 * Extracts detailed error information from any value type.
 *
 * Attempts to extract meaningful error details including:
 * - Error message (from Error.message, data.message, or string value)
 * - Error type/name (constructor name or typeof)
 * - Status code (if available)
 * - Additional context (cause chain, stack trace)
 *
 * @param error - The error value to extract details from
 * @returns Object containing extracted error details
 *
 * @example
 * const details = extractErrorDetails(apiError);
 * // { message: "User not found", type: "ApiError", status: 404, summary: "404 User not found" }
 */
export const extractErrorDetails = (error: unknown): {
  message: string;
  type: string;
  status: number | null;
  summary: string;
  cause?: unknown;
} => {
  let message = "Unknown error";
  let type: string = "unknown";
  let status: number | null = null;
  let cause: unknown = undefined;

  if (error instanceof Error) {
    message = error.message;
    type = error.name || error.constructor.name;
    cause = error.cause;

    // Check for status codes on the error itself (e.g. ApiError) or in the cause chain
    const maybeStatus = (error as unknown as { status?: unknown }).status;
    if (typeof maybeStatus === "number") {
      status = maybeStatus;
    } else if (error.cause && typeof error.cause === "object") {
      const errorObj = error.cause as Record<string, unknown>;
      if ("status" in errorObj && typeof errorObj.status === "number") {
        status = errorObj.status;
      }
    }
  } else if (typeof error === "string") {
    message = error;
  } else if (error && typeof error === "object") {
    const errorObj = error as Record<string, unknown>;
    if ("message" in errorObj && typeof errorObj.message === "string") {
      message = errorObj.message;
    }
    if ("status" in errorObj && typeof errorObj.status === "number") {
      status = errorObj.status;
    }
    if ("statusText" in errorObj) {
      message = `${message} (${errorObj.statusText})`;
    }
    type = (errorObj.constructor as any)?.name || "Object";
  }

  const summary = status ? `${status} ${message}` : message;

  return { message, type, status, summary, cause };
};

/**
 * Converts any value into an Error instance for Sentry capture.
 *
 * Sentry expects Error objects, but thrown values may be strings, objects, or other types.
 * This normalizes them while preserving the original value via the error's cause.
 *
 * @param value - The value to normalize (typically an Error, but can be any type)
 * @param fallbackMessage - Message to use if value is not an Error (default: "Unknown error")
 * @returns An Error instance with the original value captured in its cause chain
 *
 * @example
 * const error = normalizeError(apiResponse); // Converts non-Error to Error
 * const error2 = normalizeError(new Error("Known error")); // Returns as-is
 */
export const normalizeError = (
  value: unknown,
  fallbackMessage = "Unknown error"
): Error => {
  if (value instanceof Error) {
    return value;
  }

  return new Error(fallbackMessage, {
    cause: value,
  });
};

/**
 * Captures application errors to Sentry with standardized tagging and metadata.
 *
 * Used for UI layer failures (form submissions, button clicks, modal errors).
 * Automatically sets error-level severity, applies area/action/source tags,
 * and returns the normalized error for local error handling (e.g., showing toasts).
 * 
 * Automatically extracts error details for better categorization and fingerprinting.
 *
 * @param value - The error value (Error, string, or other type)
 * @param metadata - Error context including action, area, and optional extra data, tags, fingerprints
 * @returns The normalized Error instance for local error handling
 *
 * @example
 * try {
 *   await updateSubject(id, data);
 * } catch (err) {
 *   captureAppError(err, {
 *     action: "update",
 *     area: "subject",
 *     extra: { subjectId: id },
 *     fingerprint: ["subject-orchestration", "update-error"],
 *   });
 *   addErrorToast({ message: "Failed to update subject" });
 * }
 */
export const captureAppError = (
  value: unknown,
  metadata: SentryLogMetadata
): Error => {
  const error = normalizeError(
    value,
    `${metadata.area}:${metadata.action} failed`
  );

  const errorDetails = extractErrorDetails(value);

  Sentry.captureException(error, {
    level: metadata.level || "error",
    tags: {
      ...(metadata.tags || {}),
      action: metadata.action,
      area: metadata.area,
      source: metadata.source || "ui",
      ...(metadata.tags?.errorType
        ? {}
        : { errorType: errorDetails.type }),
      ...(errorDetails.status && { responseStatus: errorDetails.status.toString() }),
    },
    extra: {
      ...(metadata.extra || {}),
      errorMessage: errorDetails.message,
      errorType: errorDetails.type,
      ...(errorDetails.status ? { responseStatus: errorDetails.status } : {}),
      ...(errorDetails.cause ? { cause: String(errorDetails.cause) } : {}),
    },
    fingerprint: metadata.fingerprint || [metadata.area, metadata.action],
  });

  return error;
};

/**
 * Captures expected/non-critical issues to Sentry as warnings.
 *
 * Used for validation failures, parse errors, or conditions that don't block functionality.
 * Sets warning-level severity (less critical than captureAppError).
 * Useful for monitoring degraded states without alerting on every occurrence.
 *
 * @param name - Human-readable issue name (appears as Sentry error title)
 * @param metadata - Issue context including action, area, and optional extra data, tags, fingerprints
 *
 * @example
 * if (formValidation.errors.length > 0) {
 *   captureAppWarning("Form validation failed", {
 *     action: "validate",
 *     area: "curriculum",
 *     extra: { errorCount: formValidation.errors.length },
 *     fingerprint: ["curriculum-form", "validation-warning"],
 *   });
 * }
 */
export const captureAppWarning = (
  name: string,
  metadata: Omit<SentryLogMetadata, "level">
) => {
  Sentry.captureException(new Error(name), {
    level: "warning",
    tags: {
      ...(metadata.tags || {}),
      action: metadata.action,
      area: metadata.area,
      source: metadata.source || "ui",
    },
    extra: metadata.extra,
    fingerprint: metadata.fingerprint || [metadata.area, metadata.action],
  });
};
